import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import orderService from '../services/orderService';
import toast from 'react-hot-toast';
import {
  HiOutlineArrowLeft,
  HiOutlineCheckCircle,
  HiOutlineTruck,
  HiOutlineCurrencyDollar,
  HiOutlineXCircle,
  HiOutlinePrinter,
  HiOutlineClock,
} from 'react-icons/hi';
import './Orders.css';

const STATUS_LABELS = {
  created: 'Creada',
  accepted: 'Aceptada',
  preparing: 'Preparando',
  prepared: 'Preparada',
  in_delivery: 'En camino',
  delivered: 'Entregada',
  paid: 'Cobrada',
  closed: 'Cerrada',
  cancelled: 'Cancelada',
  deleted: 'Eliminada',
};

const STATUS_COLORS = {
  created: '#3b82f6',
  accepted: '#8b5cf6',
  preparing: '#f59e0b',
  prepared: '#06b6d4',
  in_delivery: '#f97316',
  delivered: '#22c55e',
  paid: '#14532d',
  closed: '#64748b',
  cancelled: '#ef4444',
  deleted: '#991b1b',
};

const FLOW_STEPS = ['created', 'accepted', 'preparing', 'prepared', 'in_delivery', 'delivered', 'paid', 'closed'];

const OrderDetail = () => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteReason, setDeleteReason] = useState('');
    const [deleteComments, setDeleteComments] = useState('');
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [order, setOrder] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchOrder = async () => {
    try {
      const [orderRes, historyRes] = await Promise.all([
        orderService.getById(id),
        orderService.getHistory(id).catch(() => ({ data: [] })),
      ]);
      setOrder(orderRes.data);
      setHistory(historyRes.data || []);
    } catch {
      toast.error('Error cargando orden');
      navigate('/ordenes');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchOrder();
  }, [id]);

  const handleAction = async (action, label) => {
    setActionLoading(true);
    try {
      await action(id);
      toast.success(label);
      fetchOrder();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al ejecutar acción');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePrint = async () => {
    try {
      const data = await orderService.print(id, 'html');
      const win = window.open('', '_blank');
      if (!win) {
        toast.error('Permite ventanas emergentes para imprimir');
        return;
      }
      const html = typeof data === 'string' ? data : (data?.data || data?.html || '<p>Sin datos</p>');
      const autoPrintScript = '<script>window.onload=function(){setTimeout(function(){window.print();window.close();},300);};</script>';
      const htmlToWrite = html.includes('</body>')
        ? html.replace('</body>', `${autoPrintScript}</body>`)
        : `${html}${autoPrintScript}`;
      win.document.write(htmlToWrite);
      win.document.close();
    } catch {
      toast.error('Error al imprimir');
    }
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loader"></div>
        <p>Cargando orden...</p>
      </div>
    );
  }

  if (!order) return null;

  const currentStep = FLOW_STEPS.indexOf(order.status);

  // Botones de acción según estado y rol
  const actions = [];
    // Botón para eliminar orden cerrada (solo admin)
    if (order.status === 'closed' && hasRole('admin')) {
      actions.push({
        fn: () => setShowDeleteModal(true),
        label: 'Eliminar orden cerrada',
        icon: <HiOutlineXCircle />,
        color: '#ef4444',
      });
    }
  if (order.status === 'created' && hasRole('warehouse', 'admin')) {
    actions.push({ fn: () => handleAction(orderService.accept, 'Orden aceptada'), label: 'Aceptar', icon: <HiOutlineCheckCircle />, color: '#8b5cf6' });
  }
  if (order.status === 'accepted' && hasRole('warehouse', 'admin')) {
    actions.push({ fn: () => handleAction(orderService.startPreparing, 'Preparando...'), label: 'Preparar', icon: <HiOutlineClock />, color: '#f59e0b' });
  }
  if (order.status === 'preparing' && hasRole('warehouse', 'admin')) {
    actions.push({ fn: () => handleAction(orderService.markPrepared, 'Orden lista'), label: 'Marcar Lista', icon: <HiOutlineCheckCircle />, color: '#06b6d4' });
  }
  if (order.status === 'prepared' && hasRole('delivery', 'admin')) {
    actions.push({ fn: () => handleAction(orderService.dispatch, 'Despachada'), label: 'Despachar', icon: <HiOutlineTruck />, color: '#f97316' });
  }
  if (order.status === 'in_delivery' && hasRole('delivery', 'admin')) {
    actions.push({ fn: () => handleAction(orderService.deliver, 'Entregada'), label: 'Entregada', icon: <HiOutlineCheckCircle />, color: '#22c55e' });
  }
  if (order.status === 'delivered' && !order.is_credit && hasRole('cashier', 'admin')) {
    actions.push({ fn: () => handleAction(orderService.pay, 'Cobrada'), label: 'Cobrar', icon: <HiOutlineCurrencyDollar />, color: '#14532d' });
  }
  if ((order.status === 'paid' || (order.status === 'delivered' && order.is_credit)) && hasRole('cashier', 'admin')) {
    actions.push({ fn: () => handleAction(orderService.close, 'Orden cerrada'), label: 'Cerrar', icon: <HiOutlineCheckCircle />, color: '#64748b' });
  }
  if (!['closed', 'cancelled'].includes(order.status) && hasRole('admin')) {
    actions.push({ fn: () => handleAction(orderService.cancel, 'Orden cancelada'), label: 'Cancelar', icon: <HiOutlineXCircle />, color: '#ef4444' });
  }

  return (
    <div className="order-detail">
        {/* Modal para eliminar orden cerrada */}
        {showDeleteModal && (
          <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
            <div className="modal-card" onClick={e => e.stopPropagation()}>
              <h2>Eliminar orden cerrada</h2>
              <p>Esta acción revertirá inventario, cobros, comisiones y generará nota de crédito si aplica. Se registrará en auditoría.</p>
              <div className="modal-form">
                <label>Motivo <span style={{ color: 'red' }}>*</span></label>
                <input
                  type="text"
                  value={deleteReason}
                  onChange={e => setDeleteReason(e.target.value)}
                  placeholder="Motivo de eliminación"
                  required
                />
                <label>Comentarios <span style={{ color: 'red' }}>*</span></label>
                <textarea
                  value={deleteComments}
                  onChange={e => setDeleteComments(e.target.value)}
                  placeholder="Detalles adicionales"
                  required
                />
              </div>
              <div className="modal-actions">
                <button
                  className="action-btn-lg"
                  style={{ background: '#ef4444', borderColor: '#ef4444' }}
                  disabled={actionLoading || !deleteReason || !deleteComments}
                  onClick={async () => {
                    setActionLoading(true);
                    try {
                      await orderService.deleteClosed(order.id, deleteReason, deleteComments);
                      toast.success('Orden cerrada eliminada correctamente');
                      setShowDeleteModal(false);
                      setDeleteReason('');
                      setDeleteComments('');
                      fetchOrder();
                    } catch (err) {
                      toast.error(err.response?.data?.error || 'Error al eliminar orden');
                    } finally {
                      setActionLoading(false);
                    }
                  }}
                >
                  Confirmar eliminación
                </button>
                <button className="action-btn-lg" onClick={() => setShowDeleteModal(false)}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}
      {/* Header */}
      <div className="detail-header">
        <button className="btn-back" onClick={() => navigate('/ordenes')}>
          <HiOutlineArrowLeft /> Volver
        </button>
        <div className="detail-title">
          <h1>{order.order_number}</h1>
          <span
            className="status-badge large"
            style={{
              background: `${STATUS_COLORS[order.status]}15`,
              color: STATUS_COLORS[order.status],
              borderColor: `${STATUS_COLORS[order.status]}30`,
            }}
          >
            {STATUS_LABELS[order.status]}
          </span>
        </div>
        <button className="btn-icon-sm" onClick={handlePrint} title="Imprimir">
          <HiOutlinePrinter />
        </button>
      </div>

      {/* Pipeline visual */}
      <div className="flow-pipeline">
        {FLOW_STEPS.map((step, i) => (
          <div
            key={step}
            className={`flow-step ${i <= currentStep ? 'completed' : ''} ${i === currentStep ? 'current' : ''}`}
          >
            <div className="flow-dot" style={i <= currentStep ? { background: STATUS_COLORS[step] } : {}} />
            <span className="flow-label">{STATUS_LABELS[step]}</span>
          </div>
        ))}
      </div>

      {/* Acciones */}
      {actions.length > 0 && (
        <div className="detail-actions">
          {actions.map((action, i) => (
            <button
              key={i}
              className="action-btn-lg"
              style={{ background: action.color, borderColor: action.color }}
              onClick={action.fn}
              disabled={actionLoading}
            >
              {action.icon} {action.label}
            </button>
          ))}
        </div>
      )}

      <div className="detail-grid">
        {/* Info general */}
        <div className="detail-card">
          <h3>Información General</h3>
          <div className="info-grid">
            <div className="info-row">
              <span className="info-label">Cliente</span>
              <span className="info-value">{order.client_name || `#${order.client_id}`}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Vendedor</span>
              <span className="info-value">{order.salesman_name || '-'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Tipo de Pago</span>
              <span className="info-value">
                <span className={`type-badge ${order.is_credit ? 'credit' : 'cash'}`}>
                  {order.is_credit ? 'Crédito' : 'Contado'}
                </span>
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">NCF</span>
              <span className="info-value mono">{order.ncf_number || (order.requires_ncf ? 'Pendiente' : 'N/A')}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Fecha</span>
              <span className="info-value">
                {order.created_at
                  ? new Date(order.created_at).toLocaleString('es-DO', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '-'}
              </span>
            </div>
            {order.notes && (
              <div className="info-row full">
                <span className="info-label">Notas</span>
                <span className="info-value">{order.notes}</span>
              </div>
            )}
          </div>
        </div>

        {/* Ítems */}
        <div className="detail-card">
          <h3>Productos ({order.items?.length || 0})</h3>
          {order.items && order.items.length > 0 ? (
            <table className="items-table">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Cant.</th>
                  <th>Precio</th>
                  <th>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((item, i) => {
                  const priceModified = item.original_price && Number(item.unit_price) !== Number(item.original_price);
                  return (
                  <tr key={i}>
                    <td>{item.product_name || `Producto #${item.product_id}`}</td>
                    <td className="center">{item.quantity}</td>
                    <td className="mono-cell">
                      RD$ {Number(item.unit_price || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                      {priceModified && (
                        <span style={{ display: 'block', fontSize: '11px', color: '#f59e0b', textDecoration: 'line-through' }}>
                          Original: RD$ {Number(item.original_price).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                        </span>
                      )}
                      {priceModified && (
                        <span style={{ fontSize: '10px', color: '#16a34a', fontWeight: 600 }}>✏️ Precio editado</span>
                      )}
                    </td>
                    <td className="mono-cell">RD$ {Number(item.subtotal || item.quantity * item.unit_price || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}</td>
                  </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="3" className="total-label">Subtotal</td>
                  <td className="mono-cell">RD$ {Number(order.subtotal || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}</td>
                </tr>
                <tr>
                  <td colSpan="3" className="total-label">ITBIS ({order.requires_ncf ? '18%' : '0% — Sin NCF'})</td>
                  <td className="mono-cell">RD$ {Number(order.tax_amount || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}</td>
                </tr>
                <tr className="grand-total">
                  <td colSpan="3" className="total-label">Total</td>
                  <td className="mono-cell">RD$ {Number(order.total_amount || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}</td>
                </tr>
              </tfoot>
            </table>
          ) : (
            <p className="no-items">Sin productos</p>
          )}
        </div>
      </div>

      {/* Historial */}
      {history.length > 0 && (
        <div className="detail-card">
          <h3>Historial de Estados</h3>
          <div className="history-timeline">
            {history.map((h, i) => (
              <div key={i} className="timeline-item">
                <div className="timeline-dot" style={{ background: STATUS_COLORS[h.new_status] || '#94a3b8' }} />
                <div className="timeline-content">
                  <div className="timeline-header">
                    <span className="timeline-status">{STATUS_LABELS[h.new_status] || h.new_status}</span>
                    <span className="timeline-date">
                      {new Date(h.created_at).toLocaleString('es-DO', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                  {h.notes && <p className="timeline-notes">{h.notes}</p>}
                  {h.changed_by_name && (
                    <span className="timeline-user">Por: {h.changed_by_name}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetail;
